import {useEffect, useRef, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import axios from 'axios'
import Modal from 'rsuite/Modal'
import Tag from 'rsuite/Tag'
import {toast} from 'react-toastify'
import {ReactSortable} from 'react-sortablejs'
import * as subsidiaryPage from '../index'
import {buildSortLabel} from './buildSortLabel'
import {CONTENT_TYPE_LABELS} from './contentTypeLabels'
import {hasOrderChanged, moveItem, toSortPayload} from './reorderUtils'

const API_URL = window.__RUNTIME_CONFIG__.REACT_APP_API_URL

type Row = {id: string; label: string; status: boolean}

interface Props {
  open: boolean
  handleClose: () => void
  layoutOptions: any[]
}

const ReorderSubsidiarySections = ({open, handleClose, layoutOptions}: Props) => {
  const dispatch = useDispatch()
  const {subsidiaryCategoryList} = useSelector((state: any) => state.subsidiaryCategory)
  const {loading} = useSelector((state: any) => state.subsidiaryPage)
  const categories = subsidiaryCategoryList?.subsidiaryCategory ?? []

  const [categoryId, setCategoryId] = useState('')
  const [rows, setRows] = useState<Row[]>([])
  const [fetching, setFetching] = useState(false)
  const [tooManyToReorder, setTooManyToReorder] = useState(false)
  const draggingIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (open && !categoryId && categories.length) {
      setCategoryId(categories[0].id)
    }
  }, [open, categories])

  useEffect(() => {
    if (!open || !categoryId) return
    setFetching(true)
    setTooManyToReorder(false)
    axios
      .get(`${API_URL}/subsidiary-page`, {
        params: {subsidiaryCategoryId: categoryId, page: 1, limit: 100, orderBy: 'order', order: 'asc'},
      })
      .then((res) => {
        const categoryName = categories.find((c: any) => c.id === categoryId)?.name
        const list = res?.data?.data?.subsidiaryPage ?? []
        const total = res?.data?.data?.meta?.total ?? list.length
        setTooManyToReorder(total > list.length)
        setRows(
          list.map((item: any) => {
            const component = layoutOptions.find((lm: any) => lm.id === item.layoutManagerId)
            const contentTypeLabel = component
              ? CONTENT_TYPE_LABELS[component.title] ?? component.title
              : ''
            return {
              id: item.id,
              label: buildSortLabel(categoryName, contentTypeLabel),
              status: !!item.status,
            }
          })
        )
      })
      .catch(() => {
        setRows([])
        toast.error('Could not load sections for this category. Try again.')
      })
      .finally(() => setFetching(false))
  }, [open, categoryId, layoutOptions])

  // One updating toast so repeated moves replace rather than stack.
  const notifyReorder = (label: string, position: number) => {
    const message = `${label} moved to position ${position}. Save to apply.`
    if (toast.isActive('subsidiary-reorder')) {
      toast.update('subsidiary-reorder', {render: message, autoClose: 2500})
    } else {
      toast.info(message, {toastId: 'subsidiary-reorder', autoClose: 2500})
    }
  }

  const handleMove = (from: number, to: number) => {
    const next = moveItem(rows, from, to)
    if (next === rows) return
    setRows(next)
    notifyReorder(rows[from].label, to + 1)
  }

  const handleSave = () => {
    if (!rows.length) return
    dispatch(subsidiaryPage.actions.sortSubsidiaryPage(toSortPayload(rows)))
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      className='w-75'
      backdrop='static'
      keyboard={false}
      enforceFocus={false}
      data-cy='modal'
    >
      <Modal.Header>
        <Modal.Title data-cy='modal-title'>Reorder Sections</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='mb-4'>
          <label className='mb-1 fw-bolder fs-6' htmlFor='reorder-category'>
            Subsidiary Category
          </label>
          <select
            id='reorder-category'
            className='form-select form-select-sm w-auto'
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            data-cy='reorder-category-select'
          >
            {categories.map((category: any) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        {fetching && <div className='text-muted'>Loading sections…</div>}

        {!fetching && !rows.length && (
          <div className='text-muted'>This category has no sections yet.</div>
        )}

        {!fetching && tooManyToReorder && (
          <div className='alert alert-warning' data-cy='reorder-too-many-warning'>
            This category has more sections than can be reordered here. Showing the first{' '}
            {rows.length} sections for reference only — saving is disabled because it would drop
            the rest. Please contact a developer to reorder this category.
          </div>
        )}

        {!fetching && rows.length > 0 && (
          <ReactSortable
            list={rows as any[]}
            onStart={(evt: any) => {
              // Capture the dragged row so the toast names it unambiguously;
              // diffing positions ties on adjacent moves.
              draggingIdRef.current = rows[evt.oldIndex]?.id ?? null
            }}
            setList={(next: any[]) => {
              // react-sortablejs fires setList once on mount with the same order.
              if (!hasOrderChanged(rows, next as Row[])) return
              setRows(next as Row[])
              const draggedId = draggingIdRef.current
              const newIndex = draggedId ? next.findIndex((r: any) => r?.id === draggedId) : -1
              if (newIndex >= 0) {
                notifyReorder(next[newIndex].label, newIndex + 1)
              }
              draggingIdRef.current = null
            }}
            handle='.rs-handle'
            animation={150}
          >
            {rows.map((row, index) => (
              <div
                className='d-flex align-items-center gap-3 border rounded p-3 mb-2'
                key={row.id}
                data-cy={`reorder-row-${index}`}
              >
                <span
                  className='rs-handle'
                  title='Drag to reorder'
                  style={{cursor: 'grab', fontSize: '1.2rem', color: '#9db0a5', userSelect: 'none'}}
                >
                  &#10303;
                </span>
                <span
                  className='badge bg-light text-dark'
                  style={{fontFamily: 'monospace'}}
                  title='Order on the page'
                >
                  {index + 1}
                </span>
                <span className='flex-grow-1'>{row.label}</span>
                <Tag color={row.status ? 'green' : 'red'} size='sm'>
                  {row.status ? 'Active' : 'Inactive'}
                </Tag>
                <div className='d-flex flex-column' style={{gap: '2px'}}>
                  <button
                    type='button'
                    className='btn btn-sm btn-light py-0 px-2'
                    disabled={index === 0}
                    onClick={() => handleMove(index, index - 1)}
                    title='Move up'
                    aria-label={`Move ${row.label} up`}
                  >
                    &#9650;
                  </button>
                  <button
                    type='button'
                    className='btn btn-sm btn-light py-0 px-2'
                    disabled={index === rows.length - 1}
                    onClick={() => handleMove(index, index + 1)}
                    title='Move down'
                    aria-label={`Move ${row.label} down`}
                  >
                    &#9660;
                  </button>
                </div>
              </div>
            ))}
          </ReactSortable>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button
          className={`btn btn-primary btn-sm ms-3 ${loading ? 'disabled' : ''}`}
          disabled={loading || fetching || !rows.length || tooManyToReorder}
          onClick={handleSave}
          data-cy='modal-submit'
        >
          Save
        </button>
        <button onClick={handleClose} className='btn btn-secondary btn-sm ms-3' data-cy='modal-cancel'>
          Cancel
        </button>
      </Modal.Footer>
    </Modal>
  )
}

export default ReorderSubsidiarySections
